/**
 * La semaine-type du tuteur (« Ma dispo ») : ce qui se compte sur ses heures, en un seul endroit.
 * Portage littéral du legacy `MaDispoEditor` (`DayRow`, `WeekSummary`).
 */
import { type UITutorHour, type UITutorWeekDay } from "../types/tutorDispoOffres"

const plural = (count: number, word: string) => `${word}${count > 1 ? "s" : ""}`

/**
 * Ce qu'un jour dit à côté de son nom : « 2/5 dispos · 1 live ». Le dénominateur ne compte que les
 * heures que le tuteur peut prendre — une heure qui porte un live (ou libérée) n'est pas offerte.
 * Rien quand il n'y a rien à dire.
 */
export function tutorDayCaption(hours: UITutorHour[]): string {
    const lives = hours.filter((hour) => hour.state === "live").length
    const freed = hours.filter((hour) => hour.state === "freed").length
    const taken = hours.filter((hour) => hour.state === "taken").length
    const offerable = hours.length - lives - freed

    const parts: string[] = []
    if (offerable > 0) parts.push(`${taken}/${offerable} ${plural(taken, "dispo")}`)
    if (lives > 0) parts.push(`${lives} ${plural(lives, "live")}`)
    return parts.join(" · ")
}

/**
 * Ce que la semaine fait au total : les heures qui portent un live (libérées comprises, leur live
 * court jusqu'à sa fin) et celles que le tuteur propose. Compté sur le brouillon.
 */
export function tutorWeekTotals(days: UITutorWeekDay[]): { live: number; open: number } {
    const hours = days.flatMap((day) => day.slots)
    return {
        live: hours.filter((hour) => hour.state === "live" || hour.state === "freed").length,
        open: hours.filter((hour) => hour.state === "taken").length,
    }
}

/** Le pluriel d'une tuile du résumé : « heure dispo » / « heures dispos ». */
export function tutorWeekTotalLabels(totals: { live: number; open: number }): {
    live: string
    open: string
    total: string
} {
    return {
        live: `${plural(totals.live, "heure")} avec un live`,
        open: `${plural(totals.open, "heure")} ${plural(totals.open, "dispo")}`,
        total: "au total par semaine",
    }
}
